import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, FileCheck2, IdCard, Loader2, Printer, RefreshCw, Search, Trash2, UserCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtDay = (d) =>
  d ? new Date(`${d}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "—";

/** Where the check-in came from — third-party channel check-ins get a clear badge. */
const originBadge = (it) => {
  if (it.kind === "submission" || it.checkin_via === "universal")
    return { text: "Third-party check-in · universal link", cls: "bg-[#C89B5F] text-white" };
  if (it.kind === "arrival")
    return { text: "Checked in on-site", cls: "bg-[#0F0E0E] text-[#F5EFE6]" };
  if ((it.source || "").startsWith("external:booking"))
    return { text: "Third-party check-in · Booking.com", cls: "bg-[#5B7A8C] text-white" };
  if ((it.source || "").startsWith("external:pitchup"))
    return { text: "Third-party check-in · Pitchup", cls: "bg-[#5B7A8C] text-white" };
  if ((it.source || "").startsWith("external:"))
    return { text: `Third-party check-in · ${it.source.split(":")[1]}`, cls: "bg-[#5B7A8C] text-white" };
  if (it.source === "admin-manual")
    return { text: "Manual booking check-in", cls: "bg-[#6A5F58] text-white" };
  return { text: "Website check-in", cls: "bg-[#3B8C4E]/15 text-[#3B8C4E]" };
};

/**
 * Check-ins register — every submitted online check-in: matched bookings plus
 * universal-form submissions from channel guests waiting to be assigned.
 * Also home of the universal check-in link to text/print for third-party guests.
 */
const AdminCheckIns = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [assignSel, setAssignSel] = useState({}); // submissionId -> bookingId
  const [busyId, setBusyId] = useState(null);

  const universalUrl = `${window.location.origin}/check-in`;

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/admin/checkins`);
      setData(res);
    } catch {
      toast.error("Couldn't load check-ins.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const items = useMemo(() => {
    const all = data?.items || [];
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return all;
    return all.filter((it) => {
      const hay = [it.guest_name, it.unit_label, it.vehicle_reg, it.start_date, ...(it.party_names || [])].filter(Boolean).join(" ").toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [data, q]);

  const copyUniversal = async () => {
    try {
      await navigator.clipboard.writeText(universalUrl);
      toast.success("Universal check-in link copied — text it to any guest.");
    } catch {
      toast.error("Couldn't copy — the link is " + universalUrl);
    }
  };

  const assign = async (submissionId) => {
    const bookingId = assignSel[submissionId];
    if (!bookingId) {
      toast.error("Pick the booking to attach it to first.");
      return;
    }
    setBusyId(submissionId);
    try {
      await axios.post(`${API}/admin/checkins/${submissionId}/assign`, { booking_id: bookingId });
      toast.success("Check-in attached to the booking.");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't assign.");
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (submissionId) => {
    if (!window.confirm("Delete this unmatched check-in? (The guest can submit again.)")) return;
    setBusyId(submissionId);
    try {
      await axios.delete(`${API}/admin/checkins/${submissionId}`);
      toast.success("Removed.");
      await load();
    } catch {
      toast.error("Couldn't remove.");
    } finally {
      setBusyId(null);
    }
  };

  // Office edit of a submitted check-in — e.g. someone said "not fishing"
  // online but buys a pass when they arrive. Names + fishing flags save via
  // PUT; licence photos upload per member.
  const [editFor, setEditFor] = useState(null);   // the register item
  const [editParty, setEditParty] = useState([]); // [{name, fishing}]
  const [licenceFiles, setLicenceFiles] = useState({}); // idx -> File
  const [editBusy, setEditBusy] = useState(false);

  const openEdit = (it) => {
    setEditFor(it);
    setEditParty((it.party_detail || []).map((p) => ({ name: p.name, fishing: p.fishing })));
    setLicenceFiles({});
  };

  const saveEdit = async () => {
    if (!editFor) return;
    setEditBusy(true);
    try {
      await axios.put(`${API}/admin/bookings/${editFor.id}/checkin`, { party: editParty });
      for (const [idxStr, file] of Object.entries(licenceFiles)) {
        if (!file) continue;
        const fd = new FormData();
        fd.append("license_file", file);
        await axios.post(`${API}/admin/bookings/${editFor.id}/checkin/party/${idxStr}/license`, fd);
      }
      toast.success("Check-in updated — the register shows it straight away.");
      setEditFor(null);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't update the check-in.");
    } finally {
      setEditBusy(false);
    }
  };

  const candidatesFor = (item) => {
    const cands = data?.assign_candidates || [];
    const sameDay = cands.filter((c) => c.start_date === item.start_date);
    return sameDay.length ? sameDay : cands;
  };

  return (
    <section data-testid="checkins-panel">
      <p className="overline text-[#6A5F58]"><span className="marker-line" /> Check-ins</p>
      <h2 className="font-serif text-3xl md:text-4xl mt-4 text-[#14100F]">Check-in register</h2>
      <p className="mt-3 text-[#6A5F58] text-sm max-w-3xl">
        Every online check-in that's come in — website guests and channel guests alike.
        Submissions that couldn't be matched to a booking wait here for you to assign.
      </p>

      {/* Universal link — for Booking.com / Pitchup / walk-in guests */}
      <div className="mt-6 bg-white border border-[#DDD1BE] rounded-2xl p-5" data-testid="checkins-universal">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-brand">Universal check-in link</p>
        <p className="text-xs text-[#6A5F58] mt-1.5 leading-relaxed">
          One link for every third-party guest — they pick their name, arrival date and lodge or pitch themselves.
          Text it, WhatsApp it, or print it on the welcome board.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <input readOnly value={universalUrl} onFocus={(e) => e.target.select()} className="h-10 flex-1 min-w-0 rounded-lg border border-[#DDD1BE] bg-[#F5EFE6] px-3 text-xs font-mono" data-testid="checkins-universal-url" />
          <button
            type="button"
            onClick={copyUniversal}
            className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition text-[10px] font-brand tracking-[0.12em] uppercase"
            data-testid="checkins-universal-copy"
          >
            <Copy size={12} /> Copy link
          </button>
          <a
            href="/admin/checkin-poster"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-[#B76E79] text-white hover:bg-[#0F0E0E] transition text-[10px] font-brand tracking-[0.12em] uppercase"
            data-testid="checkins-poster-link"
          >
            <Printer size={12} /> Print welcome board
          </a>
          <a
            href="/admin/posters"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full border border-[#DDD1BE] text-[#3A322E] hover:bg-[#F5EFE6] transition text-[10px] font-brand tracking-[0.12em] uppercase"
            data-testid="checkins-rules-link"
          >
            <Printer size={12} /> Rules posters
          </a>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B76E79] pointer-events-none" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, unit, reg…"
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-[#DDD1BE] bg-white text-sm"
            data-testid="checkins-search"
          />
        </div>
        <button type="button" onClick={load} className="p-2.5 rounded-full border border-[#DDD1BE] text-[#3A322E] hover:bg-[#F5EFE6]" title="Refresh" data-testid="checkins-refresh">
          <RefreshCw size={14} />
        </button>
        {data && (
          <span className="text-[11px] uppercase tracking-[0.14em] text-[#6A5F58]" data-testid="checkins-summary">
            {items.length} check-in{items.length === 1 ? "" : "s"}
            {items.filter((i) => i.kind === "submission").length > 0 && ` · ${items.filter((i) => i.kind === "submission").length} to match`}
          </span>
        )}
      </div>

      {loading && !data && (
        <div className="text-center py-10 text-[#6A5F58]"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>
      )}

      {data && items.length === 0 && (
        <div className="text-center py-10 text-[#6A5F58] border border-dashed border-[#DDD1BE] rounded-xl mt-4" data-testid="checkins-empty">
          No check-ins yet — send a link from the On-Site tab or share the universal link above.
        </div>
      )}

      <div className="mt-4 space-y-3">
        {items.map((it) => (
          <div key={`${it.kind}-${it.id}`} className={`bg-white border rounded-xl p-4 ${it.kind === "submission" ? "border-[#C89B5F]" : "border-[#DDD1BE]"}`} data-testid={`checkin-row-${it.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-[#14100F]">{it.guest_name || "—"}</p>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em] font-semibold ${originBadge(it).cls}`} data-testid={`checkin-origin-${it.id}`}>
                    {originBadge(it).text}
                  </span>
                  {it.kind === "submission" && (
                    <span className="px-2 py-0.5 rounded-full bg-[#8C2E39] text-white text-[10px] uppercase tracking-[0.12em] font-semibold" data-testid={`checkin-needs-match-${it.id}`}>
                      Needs matching
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-[#6A5F58] mt-0.5">
                  {it.unit_label} · arriving {fmtDay(it.start_date)}{it.end_date ? ` · until ${fmtDay(it.end_date)}` : ""}
                  {it.submitted_at && ` · sent ${new Date(it.submitted_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}`}
                </p>
                {(it.party_detail || []).length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`checkin-party-${it.id}`}>
                    {it.party_detail.map((p, pi) => (
                      <span key={pi} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F5EFE6] border border-[#DDD1BE] text-[10px] text-[#3A322E]" data-testid={`checkin-member-${it.id}-${pi}`}>
                        <span className="font-medium text-[#14100F]">{p.name || `Guest ${pi + 1}`}</span>
                        {p.fishing ? (
                          <span className="inline-flex items-center gap-1 text-[#3B8C4E] font-semibold">
                            · fishing
                            {p.has_license ? (
                              <a href={`${BACKEND}${p.license_url}`} target="_blank" rel="noreferrer" className="underline" data-testid={`checkin-member-licence-${it.id}-${pi}`}>licence ✓</a>
                            ) : (
                              <span className="text-[#B3394F]" data-testid={`checkin-member-licence-missing-${it.id}-${pi}`}>· licence missing</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-[#6A5F58]" data-testid={`checkin-member-notfishing-${it.id}-${pi}`}>· not fishing</span>
                        )}
                      </span>
                    ))}
                  </div>
                ) : (it.party_names || []).length > 0 && (
                  <p className="text-[11px] text-[#3A322E] mt-1">Party: {it.party_names.join(", ")}</p>
                )}
                {(it.fishing || []).length > 0 && (
                  <p className="text-[11px] text-[#3A322E] mt-0.5">
                    Fishing: {it.fishing.map((f) => `${fmtDay(f.date)} · ${f.rods} rod${f.rods > 1 ? "s" : ""}`).join(", ")}
                  </p>
                )}
                <p className="text-[11px] text-[#6A5F58] mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  {it.vehicle_reg && <span className="font-mono font-semibold text-[#14100F]">{it.vehicle_reg}</span>}
                  {it.emergency_phone && <span>Emergency: {it.emergency_phone}</span>}
                  {it.has_id_document ? (
                    it.id_document_url ? (
                      <a href={`${BACKEND}${it.id_document_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#3B8C4E] underline" data-testid={`checkin-id-link-${it.id}`}>
                        <IdCard size={11} /> ID on file — view
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[#3B8C4E]"><IdCard size={11} /> ID on file</span>
                    )
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[#C89B5F] font-semibold" data-testid={`checkin-id-missing-${it.id}`}><IdCard size={11} /> ID missing</span>
                  )}
                  {it.license_count > 0 && <span className="inline-flex items-center gap-1 text-[#3B8C4E]"><FileCheck2 size={11} /> {it.license_count} licence{it.license_count > 1 ? "s" : ""}</span>}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {it.kind === "booking" && (
                  <button
                    type="button"
                    onClick={() => openEdit(it)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#DDD1BE] text-[#3A322E] hover:bg-[#F5EFE6] text-[10px] font-brand tracking-[0.12em] uppercase"
                    data-testid={`checkin-edit-${it.id}`}
                    title="Update the check-in — e.g. a guest decided to fish on arrival"
                  >
                    <UserCheck size={11} /> Update
                  </button>
                )}
                {it.kind === "booking" && (                  <a href={`/admin/checkin/${it.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] text-[10px] font-brand tracking-[0.12em] uppercase" data-testid={`checkin-print-${it.id}`}>
                    <Printer size={11} /> Print / PDF
                  </a>
                )}
                {it.kind === "submission" && it.id_document_url && (
                  <a href={`${BACKEND}${it.id_document_url}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full border border-[#DDD1BE] text-[#3A322E] hover:bg-[#F5EFE6] text-[10px] font-brand tracking-[0.12em] uppercase" data-testid={`checkin-sub-id-${it.id}`}>
                    ID
                  </a>
                )}
                {(it.license_urls || []).map((u, i) => (
                  <a key={i} href={`${BACKEND}${u}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full border border-[#DDD1BE] text-[#3A322E] hover:bg-[#F5EFE6] text-[10px] font-brand tracking-[0.12em] uppercase" data-testid={`checkin-sub-licence-${it.id}-${i}`}>
                    Licence {i + 1}
                  </a>
                ))}
              </div>
            </div>

            {it.kind === "submission" && (
              <div className="mt-3 pt-3 border-t border-[#F0EAE0] flex flex-wrap items-center gap-2">
                <select
                  value={assignSel[it.id] || ""}
                  onChange={(e) => setAssignSel((prev) => ({ ...prev, [it.id]: e.target.value }))}
                  className="h-9 rounded-md border border-[#DDD1BE] bg-white px-2 text-xs max-w-full"
                  data-testid={`checkin-assign-select-${it.id}`}
                >
                  <option value="">Attach to booking…</option>
                  {candidatesFor(it).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.guest_name || "Guest"} — {c.unit_label} · {c.start_date} → {c.end_date}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => assign(it.id)}
                  disabled={busyId === it.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#3B8C4E] text-white hover:bg-[#0F0E0E] text-[10px] font-brand tracking-[0.12em] uppercase disabled:opacity-50"
                  data-testid={`checkin-assign-${it.id}`}
                >
                  {busyId === it.id ? <Loader2 size={11} className="animate-spin" /> : <UserCheck size={11} />} Assign
                </button>
                <button
                  type="button"
                  onClick={() => dismiss(it.id)}
                  disabled={busyId === it.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#DDD1BE] text-[#8C2E39] hover:bg-[#F5EFE6] text-[10px] font-brand tracking-[0.12em] uppercase disabled:opacity-50"
                  data-testid={`checkin-dismiss-${it.id}`}
                >
                  <Trash2 size={11} /> Dismiss
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Office edit modal — update party fishing choices / licence photos */}
      {editFor && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !editBusy && setEditFor(null)}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="checkin-edit-modal">
            <div className="p-5 bg-[#F5EFE6] border-b border-[#DDD1BE]">
              <h3 className="font-serif text-xl text-[#14100F]">Update check-in — {editFor.guest_name}</h3>
              <p className="text-[11px] text-[#6A5F58] mt-1">
                {editFor.unit_label} · {fmtDay(editFor.start_date)}{editFor.end_date ? ` → ${fmtDay(editFor.end_date)}` : ""}.
                Tick fishing for anyone who decided to fish on arrival, and attach their licence photo.
                Payment for passes is taken separately on the Arrivals tab.
              </p>
            </div>
            <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
              {editParty.map((m, i) => (
                <div key={i} className="border border-[#EDE3D2] rounded-xl p-3.5" data-testid={`checkin-edit-member-${i}`}>
                  <input
                    value={m.name}
                    onChange={(e) => setEditParty((prev) => prev.map((x, xi) => (xi === i ? { ...x, name: e.target.value } : x)))}
                    placeholder={`Guest ${i + 1} name`}
                    className="w-full h-10 rounded-lg border border-[#DDD1BE] bg-white px-3 text-sm"
                    data-testid={`checkin-edit-name-${i}`}
                  />
                  <div className="mt-2.5 flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-[#14100F] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={m.fishing}
                        onChange={(e) => setEditParty((prev) => prev.map((x, xi) => (xi === i ? { ...x, fishing: e.target.checked } : x)))}
                        className="w-4 h-4 accent-[#3B8C4E]"
                        data-testid={`checkin-edit-fishing-${i}`}
                      />
                      Fishing
                    </label>
                    {m.fishing && (
                      <label className="flex items-center gap-2 text-xs text-[#6A5F58] cursor-pointer">
                        <span className="underline">Licence photo…</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => setLicenceFiles((prev) => ({ ...prev, [i]: e.target.files?.[0] || null }))}
                          data-testid={`checkin-edit-licence-${i}`}
                        />
                        {licenceFiles[i] ? (
                          <span className="text-[#3B8C4E] font-semibold">{licenceFiles[i].name}</span>
                        ) : editFor.party_detail?.[i]?.has_license ? (
                          <a href={`${BACKEND}${editFor.party_detail[i].license_url}`} target="_blank" rel="noreferrer" className="text-[#3B8C4E] underline">current licence ✓</a>
                        ) : (
                          <span className="text-[#B3394F]">none on file</span>
                        )}
                      </label>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-[#DDD1BE] flex gap-2">
              <button onClick={() => setEditFor(null)} disabled={editBusy} className="flex-1 h-11 rounded-xl border border-[#DDD1BE] text-[#14100F] text-[11px] uppercase tracking-[0.14em] font-semibold" data-testid="checkin-edit-cancel">
                Cancel
              </button>
              <button onClick={saveEdit} disabled={editBusy} className="flex-1 h-11 rounded-xl bg-[#0F0E0E] text-[#F5EFE6] text-[11px] uppercase tracking-[0.14em] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="checkin-edit-save">
                {editBusy ? <Loader2 size={14} className="animate-spin" /> : <UserCheck size={14} />} Save update
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AdminCheckIns;
